type IdParam = number | string;

// Modern REST API Endpoints
//
// All endpoints follow RESTful conventions under /api/*
// For legacy MVC endpoints (/Controller/Action), see legacy endpoints below.

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/auth/login',
    REFRESH_TOKEN: '/api/auth/refresh-token',
  },

  // The Spring replacement for /Login/EditPassword: the shared per-company
  // transaction passwords (SpclPower, AdminPower, EditPassword, FormConfig,
  // SpclEdit) that gate privileged actions such as the QNE push.
  FORM_TRANSACTION_PASSWORD: {
    VERIFY: '/api/form-transaction-passwords/verify',
  },

  LEAVE: {
    ACTIVE: '/api/leave/active',
    SEARCH: '/api/leave/search',
  },

  JOB_TYPE: {
    LIST: '/api/job-type-master',
    GET: (id: IdParam) => `/api/job-type-master/${id}`,
    BY_COMPANY: (companyId: IdParam) => `/api/job-type-master/jobtypes/${companyId}`,
    CREATE: '/api/job-type-master',
    UPDATE: (id: IdParam) => `/api/job-type-master/${id}`,
    DELETE: (id: IdParam) => `/api/job-type-master/${id}`,
  },

  JOB_ORDER: {
    LIST: '/api/job-orders/list',
    GET: (id: IdParam) => `/api/job-orders/${id}`,
    CREATE: '/api/job-orders',
    UPDATE: (id: IdParam) => `/api/job-orders/${id}`,
    DELETE: (id: IdParam) => `/api/job-orders/${id}`,
    LOOKUPS: '/api/job-orders/lookups',
    STATUSES: '/api/job-orders/statuses',
    TYPES: '/api/job-orders/types',
    PRIORITIES: '/api/job-orders/priorities',
    NEXT_NUMBER: '/api/job-orders/next-number',
  },

  JOB_STATUS: {
    LIST: '/api/job-status-master',
    GET: (id: IdParam) => `/api/job-status-master/${id}`,
    BY_COMPANY: (companyId: IdParam) => `/api/job-status-master/select/${companyId}/`,
    CREATE: '/api/job-status-master',
    UPDATE: (id: IdParam) => `/api/job-status-master/${id}`,
    DELETE: (id: IdParam) => `/api/job-status-master/${id}`,
  },

  CUSTOMER: {
    GET: (id: IdParam) => `/api/customers/${id}`,
    SELECT: '/api/customers/select',
    LIST: '/api/customers',
    CREATE: '/api/customers',
    UPDATE: (id: IdParam) => `/api/customers/${id}`,
    SOFT_DELETE: (id: IdParam) => `/api/customers/${id}/soft-delete`,
    /** LHDN taxpayer lookup behind the customer screen's TIN button. */
    TIN_CHECK: '/api/customers/tin-check',
    /** Active customers as dropdown entries — the legacy GetCustomer. Use for combos; SELECT is the paged master list. */
    OPTIONS: '/api/customers/options',
    /** The customer list's "Push to QNE" for a customer the save's automatic push missed. */
    PUSH_QNE: (id: IdParam) => `/api/customers/${id}/push-qne`,
  },

  CUSTOMER_JOB_NOTIFICATIONS: {
    SELECT: '/api/customer-job-notifications/select',
    UPSERT: '/api/customer-job-notifications/bulk',
  },

  ADDRESS: {
    LIST: '/api/addresses',
    GET: (id: IdParam) => `/api/addresses/${id}`,
    ACTIVE_BY_COMPANY: (companyId: IdParam) => `/api/addresses/company/${companyId}/active`,
    SEARCH: (companyId: IdParam) => `/api/addresses/company/${companyId}/search`,
    CREATE: '/api/addresses',
    UPDATE: (id: IdParam) => `/api/addresses/${id}`,
    DELETE: (id: IdParam) => `/api/addresses/${id}`,
  },

  SALE_ORDER: {
    EDIT: '/api/sale-orders/edit',
    SAVE: '/api/sale-orders/save',
    UPDATE: (id: IdParam) => `/api/sale-orders/${id}`,
    QUOTATION_FORMAT2: '/api/customer-quotations/format2',
    UPDATE_STATUS: (id: IdParam) => `/api/sale-orders/${id}/status`,
    UPDATE_DO_DESCRIPTION: '/api/sale-orders/do-description',
    QUICK_UPDATE: (id: IdParam) => `/api/sale-orders/${id}/quick-update`,
    GET: (id: IdParam) => `/api/sale-orders/${id}`,
    LIST: '/api/sale-orders',
    SEARCH: '/api/sale-orders/search',
    DELETE: (id: IdParam) => `/api/sale-orders/${id}`,
    COUNT: '/api/sale-orders/count',
    JOB_VIEW: '/api/sale-orders/job-view',
    CURRENCY_VIEW: '/api/sale-orders/currency-view',
    CHECK_INVOICE: '/api/sale-orders/check-invoice',
    // Is this one job already on an invoice? Replaces the legacy
    // /SaleOrder/SelectInvoiceNumber behind the Push Invoice button, and
    // unlike it also names the invoice so the screen can offer to open it.
    INVOICE_LINK: (id: IdParam) => `/api/sale-orders/${id}/invoice-link`,
    // Create Invoice from the sale order screen: the invoice as it will be
    // saved, then save it and move the job to JOB COMPLET.
    INVOICE_PREVIEW: (id: IdParam) => `/api/sale-orders/${id}/invoice-preview`,
    CREATE_INVOICE: (id: IdParam) => `/api/sale-orders/${id}/create-invoice`,
    VESSEL_ACTIVITY_REPORT: '/api/sale-orders/vessel-activity-report',
    MISSING_REMARKS: '/api/sale-orders/missing-remarks',
    UPDATE_REMARKS: '/api/sale-orders/remarks',
  },

  BOARDING_JOB: {
    VESSEL_SCHEDULES: '/api/boarding-settlement/monthly-salary',
  },

  SALE_INVOICE: {
    MAX_NO: '/api/v1/sale-invoices/next-number',
    // Saving an invoice — Spring (replaced the old .NET InsertSaleInvoice).
    SAVE: '/api/v1/sale-invoices/save',
    EDIT_MULTI_SALE_ORDER: '/api/v1/sale-invoices/edit-multi-sale-order',
    // Soft delete (Active = 2) - Spring. No screen offers it today; it is here
    // so that whoever adds one does not reach for the old .NET URL. The four
    // legacy constants that lived in this block (EditSaleInvoice,
    // DeleteSaleInvoice, GetCurrencyValue, SelectAgentAll) had no caller and
    // were removed on 2026-09-20: editing loads through EDIT_LOAD, the currency
    // through currencyApi.
    VOID: (id: IdParam) => `/api/v1/sale-invoices/${id}`,
    CUSTOMER_JOB_NO: '/api/v1/sale-invoices/jobs',
    // LHDN MyInvois e-invoice — Spring. Replaces the legacy EInvoiceConvert
    // (and the EInvoiceConvertEdit URL the old screen referenced, which never
    // existed on any server). PUSH submits and saves the UUID; STATUS re-reads
    // LHDN's verdict and returns the share link + QR; PRINT is the invoice PDF.
    PUSH_EINVOICE: (id: IdParam) => `/api/v1/sale-invoices/${id}/push-einvoice`,
    EINVOICE_STATUS: (id: IdParam) => `/api/v1/sale-invoices/${id}/einvoice-status`,
    PRINT: (id: IdParam) => `/api/v1/sale-invoices/${id}/print`,
    // Renders the report and returns a ticket the popup can open without a
    // bearer token. The collect URL ends in the invoice number, which is what
    // the browser uses to name the download - a blob: URL names it after a
    // UUID instead. See PrintStash on the server.
    PRINT_TICKET: (id: IdParam) => `/api/v1/sale-invoices/${id}/print-ticket`,
    // One saved invoice, shaped for the entry form. Replaces the old .NET
    // /api/SaleInvoiceApp/EditSaleInvoice, which that controller never
    // implemented — it answered "No HTTP resource was found".
    EDIT_LOAD: (id: IdParam) => `/api/v1/sale-invoices/${id}/edit`,
    // The lines of one invoice, fetched when its row is expanded. The list
    // used to carry every invoice's lines whether or not anyone looked.
    LINES: (id: IdParam) => `/api/v1/sale-invoices/${id}/lines`,
    // The QNE push, run in the background. START accepts the work and returns
    // at once; STATUS reports running/done. QNE itself takes minutes, so the
    // operator is no longer held on a spinner for the whole of it.
    PUSH_QNE_START: (id: IdParam) => `/api/v1/sale-invoices/${id}/push-qne/start`,
    PUSH_QNE_STATUS: (id: IdParam) => `/api/v1/sale-invoices/${id}/push-qne/status`,
    // Sale Invoice view — Spring. VIEW replaces SelectSaleInvoice (same two
    // result sets, spelled-out filter names); PUSH_QNE replaces
    // InvoiceConvert (the QNE push; the Crystal print it opened is PRINT
    // now); MAIL replaces MailInvoice; DO_CONVERT replaces SaleOrder/DoConvert
    // and takes the SALE ORDER id, as the legacy button did.
    VIEW: '/api/v1/sale-invoices/view',
    PUSH_QNE: (id: IdParam) => `/api/v1/sale-invoices/${id}/push-qne`,
    MAIL: (id: IdParam) => `/api/v1/sale-invoices/${id}/mail`,
    DO_CONVERT: (saleOrderId: IdParam) => `/api/sale-orders/${saleOrderId}/do-convert`,
    // The legacy DO button in one call: prepare the DO, render its report and
    // return a ticket URL the report window opens (same stash as PRINT_TICKET).
    DO_PRINT_TICKET: (saleOrderId: IdParam) => `/api/sale-orders/${saleOrderId}/do-convert/print-ticket`,
    JOB_ALL_DATA: '/api/JobTypeMaster/SelectJobAllData',
    ADDRESS_SELECT: '/api/AddressMaster/SelectAddress',
    // Attachments now go through ATTACHMENTS (Spring) with folderName
    // 'SaleInvoice' — see saleInvoiceApi.fetchFiles/uploadFiles.
  },

  /**
   * Levi entries. Replaces the legacy `/LeviEntry/*` MVC actions — reads are
   * GETs with query parameters instead of POSTs carrying a `Comid` header.
   */
  LEVI_ENTRY: {
    LIST: '/api/levi-entries',
    /**
     * Entries filed against one RTI. `LIST` cannot answer this — it demands a
     * date range — and the RTI screen only knows the RTI it is editing.
     */
    BY_RTI: (rtiId: IdParam) => `/api/levi-entries/by-rti/${rtiId}`,
    NEXT_NO: '/api/levi-entries/next-no',
    RTI_OPTIONS: '/api/levi-entries/rti-options',
    GET: (id: IdParam) => `/api/levi-entries/${id}`,
    PRINT_DATA: (id: IdParam) => `/api/levi-entries/${id}/print-data`,
    /** The printed report (PDF, Jasper) for the same filters as `LIST`. */
    REPORT: '/api/levi-entries/report',
    SAVE: '/api/levi-entries',
    DELETE: (id: IdParam) => `/api/levi-entries/${id}`,
  },

  /**
   * Auto pass entries. Same shape as `LEVI_ENTRY` — the two are the same
   * document with a different number prefix, and the server serves both from
   * one implementation. Replaces the legacy `/AutoPassEntry/*` MVC actions.
   */
  AUTO_PASS_ENTRY: {
    LIST: '/api/auto-pass-entries',
    BY_RTI: (rtiId: IdParam) => `/api/auto-pass-entries/by-rti/${rtiId}`,
    NEXT_NO: '/api/auto-pass-entries/next-no',
    RTI_OPTIONS: '/api/auto-pass-entries/rti-options',
    GET: (id: IdParam) => `/api/auto-pass-entries/${id}`,
    PRINT_DATA: (id: IdParam) => `/api/auto-pass-entries/${id}/print-data`,
    /** The printed report (PDF, Jasper) for the same filters as `LIST`. */
    REPORT: '/api/auto-pass-entries/report',
    SAVE: '/api/auto-pass-entries',
    DELETE: (id: IdParam) => `/api/auto-pass-entries/${id}`,
  },

  /**
   * Incident reports — an unplanned cost the company carried: a spare dropped
   * into the sea, a truck accident, a police compound. New screen, no legacy
   * equivalent.
   *
   * `SAVE` inserts when the body has no id and updates otherwise, and `DELETE`
   * is a soft delete (Active becomes 2). Files go through `ATTACHMENT`, not
   * through here.
   */
  IR_REPORT: {
    LIST: '/api/ir',
    GET: (id: IdParam) => `/api/ir/${id}`,
    SAVE: '/api/ir',
    DELETE: (id: IdParam) => `/api/ir/${id}`,
    STATUSES: '/api/ir/statuses',
    DEPARTMENTS: '/api/ir/departments',
  },

  /**
   * Record attachments. One endpoint replaces the legacy `/Common/UploadFile`,
   * `UploadFile2`, `UploadFile3`, `UploadFile5` and `FetchFile2` actions, which
   * shared an algorithm and differed only in which file types they compressed —
   * now the `mode` parameter. See `src/features/attachments`.
   */
  ATTACHMENTS: {
    LIST: '/api/attachments',
    UPLOAD: '/api/attachments',
    DELETE: '/api/attachments',
    LIMITS: '/api/attachments/limits',
    SUMMARY: '/api/attachments/summary',
  },

  COMMON: {
    /**
     * Which record ids under an upload folder actually hold a file.
     * Takes `imageDirectory` as a query param, e.g. `/6/PayBills`.
     */
    CHECK_FILES: '/api/common/checkFiles',
    FETCH_FILES: '/api/common/fetchFiles',
  },

  SEQUENCE: {
    MAX_SEQUENCE: (companyId: IdParam) => `/api/sequence-masters/company/${companyId}/max-sequence`,
  },

  JOB_ALL_DATA: {
    SELECT: '/api/job-type-master/select-all-data',
  },

  PRODUCT: {
    LIST: '/api/product-masters',
    GET: (id: IdParam) => `/api/product-masters/${id}`,
    BY_COMPANY: (companyId: IdParam) => `/api/product-masters/company/${companyId}`,
    ACTIVE_BY_COMPANY: (companyId: IdParam) => `/api/product-masters/company/${companyId}/active`,
    PRODUCT_LIST: (companyId: IdParam) => `/api/item-masters/company/${companyId}/products`,
    SEARCH: (companyId: IdParam) => `/api/product-masters/company/${companyId}/search`,
    BY_CODE: (prodCode: IdParam, companyId: IdParam) =>
      `/api/product-masters/code/${prodCode}?companyRefId=${companyId}`,
    CREATE: '/api/product-masters',
    UPDATE: (id: IdParam) => `/api/product-masters/${id}`,
    DELETE: (id: IdParam) => `/api/product-masters/${id}`,
    ACTIVATE: (id: IdParam) => `/api/product-masters/${id}/activate`,
    DEACTIVATE: (id: IdParam) => `/api/product-masters/${id}/deactivate`,
    /** Saves every edited row in one transaction. */
    BATCH: '/api/product-masters/batch',
  },

  PORT: {
    ACTIVE_BY_COMPANY: (companyId: IdParam) => `/api/port-masters/company/${companyId}/active`,
  },

  EMPLOYEE_PORT: {
    BULK_INSERT: '/api/employee-ports/bulk',
    GET_BY_EMPLOYEE: (companyId: IdParam, employeeId: IdParam) => `/api/employee-ports/company/${companyId}/employee/${employeeId}`,
  },

  LOCATION: {
    ACTIVE_BY_COMPANY: (companyId: IdParam) => `/api/location-master/company/${companyId}/active`,
  },

  PRE_ALERT_MASTER: {
    ACTIVE_BY_COMPANY: (companyId: IdParam) => `/api/transaction/pre-alert/masters/company/${companyId}/active`,
    BY_DATE_RANGE: (companyId: IdParam) => `/api/transaction/pre-alert/masters/company/${companyId}/date-range`,
    DETAILS: (id: IdParam) => `/api/transaction/pre-alert/masters/${id}/details`,
    DELETE: (id: IdParam) => `/api/transaction/pre-alert/masters/${id}`,
    ACTIVATE: (id: IdParam) => `/api/transaction/pre-alert/masters/${id}/activate`,
    DEACTIVATE: (id: IdParam) => `/api/transaction/pre-alert/masters/${id}/deactivate`,
  },

  PRE_ALERT_TRANSACTION: {
    REPORT: '/api/transaction/pre-alert/report',
    INSERT: '/api/transaction/pre-alert/insert',
    EDIT: (id: IdParam) => `/api/transaction/pre-alert/${id}`,
    SELECT: '/api/transaction/pre-alert/select',
    MAX_NO: '/api/transaction/pre-alert/max-report-no',
    /** The Pre Alert report PDF behind a ticket - the Jasper port of CRPreAlertRerport / CRPreAlertRerportCons. */
    REPORT_PDF_TICKET: '/api/transaction/pre-alert-report/ticket',
    /** A saved pre-alert's PDF behind a ticket (the F5 list's row icon). ?companyId= */
    SAVED_REPORT_PDF_TICKET: (id: IdParam) => `/api/transaction/pre-alert-report/saved/${id}/ticket`,
  },

  TAX: {
    ACTIVE_BY_COMPANY: (companyId: IdParam) => `/api/tax-masters/company/${companyId}/active`,
  },

  /**
   * The sale order forwarding report (legacy `Report/SaleOrderFWReport`).
   *
   * Six legacy actions split across `TransactionReport` and `SaleOrder` — they
   * all belong to this one screen, so Spring keeps them under one path.
   *
   * There is deliberately no Excel *download* endpoint. Legacy's
   * `SelectSaleOrderFWExcel` ran the grid's own query again, wrote an .xlsx into
   * a folder on the web server and handed back a URL to navigate to; the screen
   * now exports the rows it is already showing, so no server file is involved.
   */
  /**
   * ZB entries — legacy `Billing/ZBVIEW` (add/edit) and `Billing/ZBALLVIEW` (list).
   *
   * The save goes through `SP_ZBEntryMaster`, which owns the insert-vs-update
   * decision and the transaction, so one endpoint covers both.
   */
  ZB_ENTRY: {
    SEARCH: '/api/zb-entries',
    GET: (id: IdParam) => `/api/zb-entries/${id}`,
    SAVE: '/api/zb-entries/bulk-save',
  },

  /** HS code rate master — legacy `ForwardingInvenrory/HscoderaesIndex`. */
  HS_CODE_RATES: {
    LIST: '/api/hs-code-rates',
    SAVE: '/api/hs-code-rates/bulk-save',
    DELETE: (id: IdParam) => `/api/hs-code-rates/${id}`,
  },

  SALE_ORDER_FORWARDING_REPORT: {
    SEARCH_FORWARDING: '/api/sale-order-forwarding-report/forwarding/search',
    SEARCH_ZB: '/api/sale-order-forwarding-report/zb/search',
    /** Printed reports (PDF, Jasper) for the same body as the two searches. */
    REPORT_FORWARDING: '/api/sale-order-forwarding-report/forwarding/report',
    REPORT_ZB: '/api/sale-order-forwarding-report/zb/report',
    S1_OPTIONS: (comId: IdParam) => `/api/sale-order-forwarding-report/s1-options/${comId}`,
    VESSEL_NAMES: (comId: IdParam) => `/api/sale-order-forwarding-report/vessel-names/${comId}`,
    UPDATE_FORWARDING_DATE: '/api/sale-order-forwarding-report/forwarding-date',
    EXCEL_IMPORT: '/api/sale-order-forwarding-report/excel-import',
  },

  /**
   * Forwarding requests (backend openspec change `forwarding-requests`):
   * Customer Service asks for customs forms on a sale order, the forwarding
   * team ticks them on its planning page. Independent of the forwarding legs.
   */
  FORWARDING_REQUESTS: {
    CREATE: '/api/forwarding-requests',
    BY_SALE_ORDER: (saleOrderId: IdParam) => `/api/forwarding-requests/sale-order/${saleOrderId}`,
    SEARCH: '/api/forwarding-requests/search',
    /** One row's Save: both ticks and the C Number together. */
    TICKS: (id: IdParam) => `/api/forwarding-requests/${id}/ticks`,
    CANCEL: (id: IdParam) => `/api/forwarding-requests/${id}/cancel`,
  },

  /**
   * End customers (backend openspec change `sale-order-end-customer`): the customer of our
   * customer (Daikin on a CIVA job), kept once per company and linked to customers.
   */
  END_CUSTOMERS: {
    LIST: '/api/end-customers',
    CREATE: '/api/end-customers',
    UPDATE: (id: IdParam) => `/api/end-customers/${id}`,
    /** GET: the customer's linked end customers. PUT: replace them. */
    FOR_CUSTOMER: (customerId: IdParam) => `/api/end-customers/customer/${customerId}`,
    /** "+ Add new" on the sale order: find or create by name, then link. */
    QUICK_ADD: (customerId: IdParam) => `/api/end-customers/customer/${customerId}/quick-add`,
  },

  /** The signed-in employee's in-app bell (backend change `forwarding-request-followup`). */
  EMPLOYEE_NOTIFICATIONS: {
    MINE: '/api/employee-notifications/mine',
    READ: (id: IdParam) => `/api/employee-notifications/${id}/read`,
    READ_ALL: '/api/employee-notifications/mine/read-all',
  },

  EMPLOYEE: {
    LIST: '/api/employees',
    GET: (id: IdParam) => `/api/employees/${id}`,
    BY_COMPANY: (companyId: IdParam) => `/api/employees/company/${companyId}/all`,
    BY_COMPANY_ROLES: (companyId: IdParam) => `/api/employees/company/${companyId}/roles`,
    SEARCH: '/api/employees/search',
    TYPES: '/api/employees/types',
    BULK_INSERT: (companyId: IdParam) => `/api/employees/bulk/${companyId}`,
  },

  PROFILE: {
    ME: '/api/profile/me',
    PASSWORD: '/api/profile/me/password',
  },

  CURRENCY: {
    VALUE: '/api/currency-value/get',
  },

  SYMBOL: {
    SELECT_BY_COMPANY: (companyId: IdParam) => `/api/symbol-masters/select/company/${companyId}`,
  },

  /** Customer Statement of Account — the migrated /Report/CustomerStatement screen. */
  CUSTOMER_STATEMENT: {
    QUERY: '/api/customer-statements/query',
    /** Renders the PDF under the caller's token and answers with a ticket URL the report window can open. */
    PRINT_TICKET: '/api/customer-statements/print-ticket',
    /** Mails one customer's statement with the PDF attached. */
    SEND: '/api/customer-statements/send',
    /** The mail Send would build (subject, HTML body, To, CC) for the preview - nothing is sent. */
    MAIL_PREVIEW: '/api/customer-statements/mail-preview',
    /** The statement as an .xlsx workbook (POST the filters; one sheet per customer). */
    EXCEL: '/api/customer-statements/excel',
    /** Bulk run: start (POST) and the run in progress for a company (GET ?companyId=). */
    MAIL_JOBS: '/api/customer-statements/mail-jobs',
    MAIL_JOBS_ACTIVE: '/api/customer-statements/mail-jobs/active',
    MAIL_JOB: (id: string) => `/api/customer-statements/mail-jobs/${encodeURIComponent(id)}`,
    MAIL_JOB_CANCEL: (id: string) => `/api/customer-statements/mail-jobs/${encodeURIComponent(id)}/cancel`,
    /** When each customer of a company last received a statement mail (GET ?companyId=). */
    LAST_SENT: '/api/customer-statements/last-sent',
    /** Customers' replies read from the accounts mailbox, and answering them. */
    REPLIES_LATEST: '/api/customer-statements/replies/latest',
    REPLIES_CONVERSATION: '/api/customer-statements/replies/conversation',
    REPLIES_SEND: '/api/customer-statements/replies/send',
    REPLIES_SYNC: '/api/customer-statements/replies/sync',
    /** The header bell: unread replies (GET ?companyId=&limit=); role-gated server-side. */
    REPLIES_UNREAD: '/api/customer-statements/replies/unread',
    REPLY_READ: (id: number) => `/api/customer-statements/replies/${id}/read`,
    REPLY_ATTACHMENT: (id: number, attachmentId: number) => `/api/customer-statements/replies/${id}/attachments/${attachmentId}`,
  },

  /** Country master, for the customer and supplier address pickers. */
  COUNTRY: {
    LIST: '/api/country-masters',
  },

  /**
   * Payment terms master. Note this is NOT `PAYMENT_TERMS.SELECT_ALL` above -
   * that one is the legacy-shaped endpoint; this is the CRUD list the master
   * screens read, and it returns every company's rows, so filter by company.
   */
  PAYMENT_TERMS_MASTER: {
    /** Every company's rows. Use SELECT_BY_COMPANY for a dropdown. */
    LIST: '/api/payment-terms-master',
    /** Active terms for one company — the legacy SelectPaymentTerms {Comid}. */
    SELECT_BY_COMPANY: (companyId: IdParam) => `/api/payment-terms-master/company/${companyId}`,
  },

  // Expense Master - Spring. GET ?companyId=&includeInactive=, POST, PUT /{id},
  // DELETE /{id}?companyId= (soft: Active = 2). Replaced the legacy
  // /ExpenseMaster/SelectExpense | InsertExpense | DeleteExpenseType.
  // Raises the month's pending payments (one bill per active sub expense for
  // this month and next). POST ?companyId= ; idempotent. Replaced the legacy
  // /Login/InsertPendingPayment that the .NET login ran on every sign-in.
  PENDING_PAYMENT: {
    GENERATE: '/api/pending-payments/generate',
    // The Payment Pending screen. BOARD: GET ?companyId=&month=yyyy-MM&
    // includeEarlierUnpaid= (legacy /TransactionReport/SelectPendingPayment).
    // SETTLEMENT: PUT {companyId, paid, paidAmount, paidDate, amount}
    // (legacy /TransactionReport/InsertppaymentpendingAmount).
    BOARD: '/api/pending-payments/board',
    SETTLEMENT: (id: IdParam) => `/api/pending-payments/${id}/settlement`,
    // AMOUNT: PUT {companyId, amount} - the same legacy call with the tick off.
    AMOUNT: (id: IdParam) => `/api/pending-payments/${id}/amount`,
    // SETTLE_MANY: POST {companyId, ids, remarks} - many old bills ticked paid at once, one reason.
    SETTLE_MANY: '/api/pending-payments/settle-many',
    // PAYABLE: GET ?companyId= - the bill as a payment voucher line (account, what is still owed).
    PAYABLE: (id: IdParam) => `/api/pending-payments/${id}/payable`,
    // RECALCULATE: POST ?companyId= - every live bill re-read against its active vouchers.
    RECALCULATE: '/api/pending-payments/recalculate',
  },

  // Period lock + audit trail of the accounts documents. Off on a server until
  // db/accounts-controls.sql is run and maleva.accounts-controls.enabled=true.
  ACCOUNTS_CONTROLS: {
    PERIODS: '/api/accounts-controls/periods',
    CLOSE: (month: string) => `/api/accounts-controls/periods/${month}/close`,
    REOPEN: (month: string) => `/api/accounts-controls/periods/${month}/reopen`,
    AUDIT: '/api/accounts-controls/audit',
  },

  // Payable reports - all GET, read-only. ageing ?asAt=yyyy-MM-dd; the others ?from=&to=.
  PAYABLE_REPORTS: {
    AGEING: '/api/payable-reports/ageing',
    HIRE_PURCHASE: '/api/payable-reports/hire-purchase',
    PAYMENT_REGISTER: '/api/payable-reports/payment-register',
    EXCEPTIONS: '/api/payable-reports/exceptions',
  },

  // Sub Expense master (legacy /SubExpense/* + SP_SubExpense). One sub expense a
  // request, the id in the path; DELETE and RETIRE are Active = 2.
  SUB_EXPENSE: {
    BASE: '/api/sub-expenses',
    LOOKUPS: '/api/sub-expenses/lookups',
    BY_ID: (id: IdParam) => `/api/sub-expenses/${id}`,
    RETIRE: '/api/sub-expenses/retire',
  },

  // Sub expenses as contracts (payee, agreement, HP split, accounts, dated
  // amount changes, loan schedule). Needs db/accounts-structure.sql.
  SUB_EXPENSE_CONTRACT: {
    BASE: '/api/sub-expense-contracts',
    LOOKUPS: '/api/sub-expense-contracts/lookups',
    BY_ID: (id: IdParam) => `/api/sub-expense-contracts/${id}`,
    AMOUNT: (id: IdParam) => `/api/sub-expense-contracts/${id}/amount`,
    AMOUNT_HISTORY: (id: IdParam) => `/api/sub-expense-contracts/${id}/amount-history`,
    SCHEDULE: (id: IdParam) => `/api/sub-expense-contracts/${id}/schedule`,
    HEAD_CATEGORY: (id: IdParam) => `/api/sub-expense-contracts/expense-heads/${id}/category`,
  },

  EXPENSE_MASTER: {
    BASE: '/api/expense-masters',
    BY_ID: (id: IdParam) => `/api/expense-masters/${id}`,
  },

  // Customer Service + Transport dashboards — Spring, replacing the legacy
  // /api/DashBoardApp/* Web API on 2026-09-20. All GET; dates are yyyy-MM-dd;
  // rows keep the legacy column names. Call through `api`.
  // (The similarly named DASHBOARD.VESSEL_PLANNING / SALES_ORDER_STATUS /
  // EMPLOYEE_RULES belong to features/dashboard and are a different port.)
  // Any employee's Customer Service dashboard (openspec cs-team-dashboards). The server takes the
  // company and the caller from the token; employeeId (default: the caller) must be in their branch.
  //   scope=me (own work, default) | team (the whole branch, with a breakdown).
  TEAM_DASHBOARD: {
    VIEW: '/api/team-dashboard/view',
    DIRECTORY: '/api/team-dashboard/directory',
    SEARCH: '/api/team-dashboard/search',
    PICKUPS: '/api/team-dashboard/pickups',
    VESSELS: '/api/team-dashboard/vessels',
    JOB_COUNTS: '/api/team-dashboard/job-counts',
    STATUS_SUMMARY: '/api/team-dashboard/status-summary',
  },

  // The customer-service job board (openspec cs-job-board): sale orders in computed lanes, inline
  // edits, quick actions and a per-job timeline. Caller and company come from the token.
  CS_BOARD: {
    BOARD: '/api/cs-board',
    FIELDS: (id: IdParam) => `/api/cs-board/${id}/fields`,
    EVENTS: (id: IdParam) => `/api/cs-board/${id}/events`,
    TIMELINE: (id: IdParam) => `/api/cs-board/${id}/timeline`,
    EXPORT: '/api/cs-board/export',
    // Enquiries: the customer's mail typed before a job number exists.
    ENQUIRIES: '/api/cs-board/enquiries',
    ENQUIRY_FIELDS: (id: IdParam) => `/api/cs-board/enquiries/${id}/fields`,
    ENQUIRY_CANCEL: (id: IdParam) => `/api/cs-board/enquiries/${id}/cancel`,
    ENQUIRY_CONFIRM: (id: IdParam) => `/api/cs-board/enquiries/${id}/confirm`,
    ENQUIRY_UNLINK: (id: IdParam) => `/api/cs-board/enquiries/${id}/unlink`,
    ENQUIRY_TIMELINE: (id: IdParam) => `/api/cs-board/enquiries/${id}/timeline`,
    // Row colours (the Excel fills) and the company's legend for them.
    MARK: (kind: string, id: IdParam) => `/api/cs-board/${kind}/${id}/mark`,
    LEGEND: '/api/cs-board/legend',
    // The old Excel closing sheets into the system (Super Admin; ?apply=true writes).
    IMPORT_EXCEL: '/api/cs-board/import/excel',
    // Vessel legs of a job: offloaded from one vessel, loaded onto many, or the other way round.
    LEGS: (id: IdParam) => `/api/cs-board/${id}/legs`,
    LEG: (id: IdParam, legId: IdParam) => `/api/cs-board/${id}/legs/${legId}`,
    // The ZB tracker (the Excel "ZB TRACKER" sheets): one shared list per company.
    ZB: '/api/cs-board/zb',
    ZB_ROW: (id: IdParam) => `/api/cs-board/zb/${id}`,
    ZB_WRITE: (id: IdParam) => `/api/cs-board/zb/${id}/write-to-job`,
  },

  OPERATIONS_DASHBOARD: {
    PICKUPS: '/api/operations-dashboard/pickups',
    VESSELS: '/api/operations-dashboard/vessels',
    JOBS: '/api/operations-dashboard/jobs',
    JOB_COUNTS: '/api/operations-dashboard/job-counts',
    STATUS_SUMMARY: '/api/operations-dashboard/status-summary',
    TEAM: '/api/operations-dashboard/team',
  },

  // Quotation — Spring (/api/quotations), replacing the legacy
  // /api/QuotationApp/* Web API on 2026-09-20. Call these through `api`
  // (VITE_API_BASE_URL).
  //   POST BASE = create, PUT BY_ID = update, GET BY_ID = edit/copy,
  //   GET BY_NUMBER = F3 (the bare number, 42 — not QT0042/2026),
  //   DELETE BY_ID = soft delete.
  QUOTATION: {
    BASE: '/api/quotations',
    NEXT_NUMBER: '/api/quotations/next-number',
    BY_ID: (id: IdParam) => `/api/quotations/${id}`,
    BY_NUMBER: (number: IdParam) => `/api/quotations/by-number/${number}`,
    SEARCH: '/api/quotations/search',
    // The printed quotation (Jasper; legacy QuotationApp/SelectQuotationReport):
    // answers with a ticket Url for the report window. ?companyId=&simple=
    REPORT_TICKET: (id: IdParam) => `/api/quotations/${id}/pdf-ticket`,
  },

  // The Report menu's master lists (Jasper; legacy MasterReport/*ReportView + ReportViewer):
  // address, agent, customer, driver, employee, item, product, supplier.
  //   REPORT_TICKET answers with a ticket Url for the report window (?companyId= + the page's filters).
  //   LOOKUP(list) is one of the pages' dropdowns, as [{ value, label }].
  MASTER_REPORT: {
    REPORT_TICKET: (report: string) => `/api/master-reports/${report}/ticket`,
    LOOKUP: (list: string) => `/api/master-reports/lookups/${list}`,
    // The truck / driver expiry lists as data (legacy MasterReport/TruckReportView, DriverReportView).
    //   ?companyId=&until=  (+ apadBonamUntil=&serviceUntil= for trucks); ISO dates.
    TRUCK_ROWS: '/api/master-reports/trucks/rows',
    DRIVER_ROWS: '/api/master-reports/drivers/rows',
  },

  // The company's licences with their expiry (legacy LicenseMaster/SelectLicense).
  //   ?companyId=[&search=&category=&from=&to=]
  LICENSE_MASTER: {
    ROWS: '/api/license-master/rows',
  },

  // The Report menu's sale reports (Jasper; legacy SaleReport/* + ReportViewer): sale, sale-detailed,
  // sale-credit, sale-credit-detailed. ?companyId=&fromDate=&toDate=&customerId=&documentNo=
  SALES_REPORT: {
    REPORT_TICKET: (report: string) => `/api/sales-reports/${report}/ticket`,
  },

  // The Report menu's customer reports (Jasper; legacy CustomerReport/* + ReportViewer): balance, ageing,
  // receipt, receipt-due, quotation. EXCEL (.xlsx) for balance and quotation.
  CUSTOMER_REPORT: {
    REPORT_TICKET: (report: string) => `/api/customer-reports/${report}/ticket`,
    EXCEL: (report: string) => `/api/customer-reports/${report}/excel`,
  },

  // The Report menu's supplier reports (Jasper; legacy SupplierReport/* + ReportViewer): balance, payment,
  // payment-due, statement.
  SUPPLIER_REPORT: {
    REPORT_TICKET: (report: string) => `/api/supplier-reports/${report}/ticket`,
  },

  // The Report menu's purchase and bills reports (Jasper; legacy PurchaseReport/* and BillsReport/* +
  // ReportViewer): purchase, purchase-detailed, purchase-item-wise, bills, bills-detailed, bills-item-wise.
  PURCHASE_REPORT: {
    REPORT_TICKET: (report: string) => `/api/purchase-reports/${report}/ticket`,
  },

  /** The Expense Report page; `searchBy` picks Fetch All / Detailed / Due Report / Consolidated. */
  EXPENSE_REPORT: {
    TICKET: '/api/expense-reports/ticket',
  },

  /** The Payment Voucher Report page; `detailed` prints the detailed layout, `bank` adds the bank details. */
  PAYMENT_VOUCHER_REPORT: {
    TICKET: '/api/payment-voucher-reports/ticket',
  },

  SALARY_REPORT: {
    TICKET: '/api/salary-reports/ticket',
  },

  /** Employee Salary Report (HR dashboard): driver salary, boarding officer or customs officer report. */
  EMPLOYEE_SALARY_REPORT: {
    TICKET: '/api/employee-salary-reports/ticket',
  },

  /** Fuel Report and Renewal Report: report is `fuel` or `renewal`. */
  OPERATIONS_REPORT: {
    REPORT_TICKET: (report: string) => `/api/operations-reports/${report}/ticket`,
  },

  TRUCK: {
    COMBO: '/api/truck-combo',
    ALL_DETAIL_COMBO: '/api/truck-masters/alltruckdetatilcombo',
    BY_COMPANY: (companyId: IdParam) => `/api/truck-masters/company/${companyId}`,
    GET: (id: IdParam) => `/api/truck-masters/${id}`,
    /** The screens' delete: Active = 2 on the company's truck (legacy /TruckMaster/DeleteTruck). */
    SOFT_DELETE: (id: IdParam) => `/api/truck-masters/${id}/soft-delete`,
    PROCESS: '/api/truck-masters/process',
    /** The bookable fleet with each truck's driver and phone number - the header's truck/driver window. */
    FLEET_DRIVERS: '/api/truck-masters/fleet-drivers',
    /** Puts a truck on the road, in the workshop, or marks it sold. Two fields only. */
    UPDATE_STATUS: (id: IdParam) => `/api/truck-masters/${id}/status`,
    /** Sets, moves or clears the driver who normally drives this truck.
     *  The pairing lives on DriverMaster.TruckRefId - this writes it there. */
    ASSIGN_DRIVER: (id: IdParam) => `/api/truck-masters/${id}/driver`,
    LICENSE_CHECK: (truckId: IdParam) => `/api/trucks/${truckId}/license-check`,
    SEARCH_PAGINATED: '/api/truck-masters/search',
    /** The truck expiry report (PDF, Jasper): every truck with a date due by `until`. */
    EXPIRY_REPORT: '/api/truck-masters/expiry-report',
  },

  DRIVER: {
    LIST: '/api/driver-masters',
    COMBO: '/api/driver-combo',
    SELECT_ALL_DETAILS: '/api/driver-masters/selectalldriverDetails',
    BY_COMPANY: (companyId: IdParam) => `/api/drivers/company/${companyId}`,
    GET: (id: IdParam) => `/api/drivers/${id}`,
    SEARCH_PAGINATED: '/api/driver-masters/search',
    /** One driver master record, for the add / edit form. */
    MASTER_GET: (id: IdParam) => `/api/driver-masters/${id}`,
    /** Insert or update; allocates the driver number for a new record. */
    PROCESS: '/api/driver-masters/process',
    /** Soft delete: marks Active = 2, which the driver search excludes. */
    MASTER_DELETE: (id: IdParam) => `/api/driver-masters/${id}`,
    /** Brings a soft-deleted driver back as active. */
    MASTER_RESTORE: (id: IdParam) => `/api/driver-masters/${id}/restore`,
  },

  AGENT_COMPANY: {
    BY_COMPANY: (companyId: IdParam) => `/api/agent-companies/company/${companyId}`,
  },
  AGENT: {
    SELECT_ALL: (companyRefId: IdParam, jobId: IdParam) =>
      `/api/agents/select-all?companyRefId=${companyRefId}&jobId=${jobId}`,
  },

  PLANNING: {
    LIST: '/api/planing/list',
    GET: (id: IdParam) => `/api/planning-masters/${id}`,
    EDIT: '/api/planing/edit',
    MAX_NO: (companyId: IdParam) => `/api/planing/max-planning-no/${companyId}`,
    SELECT: '/api/planing/select-planning',
    SAVE: '/api/planing/save',
    SAVE_REMARKS: (id: IdParam) => `/api/planing/${id}/remarks`,
    UPDATE: '/api/planing/update',
    DELETE: (id: IdParam) => `/api/planing/${id}`,
    UPDATE_DATES: '/api/planing/update-dates',
    SEARCH: '/api/planing/search',
    SORT: '/api/planing/sort',
    PUSH_RTI: '/api/planing/push-rti',
    /** What "create RTI for the whole plan" would do. Reads only, writes nothing. */
    RTI_BATCH_PREVIEW: (planningId: IdParam, companyId: IdParam) =>
      `/api/planing/${planningId}/rti-batch/preview?companyId=${companyId}`,
    /** Creates the confirmed RTIs for a plan — all of them, or none. */
    RTI_BATCH: (planningId: IdParam) => `/api/planing/${planningId}/rti-batch`,
    FORWARDING_REPORT: '/api/planning/reports/forwarding',
    /** The Planning report (one plan's jobs), PDF - the Jasper port of CRPlaning. */
    REPORT: (planningId: IdParam) => `/api/planning/reports/${planningId}/pdf`,
    /** The same report parked behind a ticket whose link ends in the planning number (download name). */
    REPORT_TICKET: (planningId: IdParam) => `/api/planning/reports/${planningId}/pdf-ticket`,
  },

  // WhatsApp + voice calls - Spring integration/whatsapp (legacy commonfunctions.WhatsAppSend / PhoneCall)
  NOTIFICATIONS: {
    STATUS: '/api/notifications/status',
    WHATSAPP: '/api/notifications/whatsapp',
    WHATSAPP_PDF: '/api/notifications/whatsapp/pdf',
    WHATSAPP_GROUPS: '/api/notifications/whatsapp/groups',
    VOICE_CALL: '/api/notifications/voice-call',
  },

  VESSEL_PLANNING: {
    LIST_BY_COMPANY: (companyId: IdParam) => `/api/vessel-plannings/company/${companyId}/active`,
    GET: (id: IdParam) => `/api/vessel-plannings/${id}`,
    MAX_NO: (companyId: IdParam) => `/api/vessel-plannings/max-vessel-planning-no/${companyId}`,
    SELECT: '/api/vessel-plannings/select-vessel-planning',
    EDIT: '/api/vessel-plannings/edit',
    SEARCH: '/api/vessel-plannings/search',
    SAVE: '/api/vessel-plannings/save',
    VIEW: '/api/vessel-plannings/view',
    SALE_ORDER_UPDATE: '/api/vessel-plannings/sale-order-update',
    SALE_ORDER_UPDATE_MANY: '/api/vessel-plannings/sale-order-update-many',
    DELETE: (id: IdParam) => `/api/vessel-plannings/${id}`,
    /** The Vessel Planning report (one card per job), PDF - the Jasper port of CRPreAlertRerportDetail. */
    REPORT: (id: IdParam) => `/api/vessel-plannings/${id}/report`,
    /** The same report parked behind a ticket whose link ends in the plan number (download name). */
    REPORT_TICKET: (id: IdParam) => `/api/vessel-plannings/${id}/report-ticket`,
  },

  /** Vessel names the company uses, with PTP's calls (backend change `vessel-master-and-ptp-schedule`). */
  VESSEL_MASTER: {
    LIST: '/api/vessel-masters',
    SUGGEST: '/api/vessel-masters/suggest',
    SIMILAR: '/api/vessel-masters/similar',
    UPDATE: (id: IdParam) => `/api/vessel-masters/${id}`,
    MERGE: (id: IdParam) => `/api/vessel-masters/${id}/merge`,
    /** Links a saved sale order's two legs to the master (and a picked PTP call). */
    LINKS: (saleOrderId: IdParam) => `/api/sale-orders/${saleOrderId}/vessel-links`,
  },

  /** Jobs whose ETA, ETD or SCN differ from PTP's schedule. */
  VESSEL_CHANGES: {
    LIST: '/api/vessel-changes',
    DISMISS: '/api/vessel-changes/dismiss',
    /** Open vessel alerts: mine, and my team's escalated to me; gone once the jobs are handled. */
    ALERTS: '/api/vessel-changes/alerts',
    /** "Not this vessel": closes a LINK alert (mine or my team member's). */
    ALERT_CLOSE: (id: number) => `/api/vessel-changes/alerts/${id}/close`,
  },

  /** PTP's published vessel schedule, read only. */
  PTP_SCHEDULE: {
    LIST: '/api/ptp/vessel-schedules',
    READ_NOW: '/api/ptp/vessel-schedules/read',
  },

  /** Westports' schedule, from the eTP "Vessel Enquiry" Excel that Customer Service uploads (change westports-vessel-schedule). */
  WESTPORTS_SCHEDULE: {
    LIST: '/api/westports/vessel-schedules',
    UPLOAD: '/api/westports/vessel-schedules/upload',
    UPLOADS: '/api/westports/vessel-schedules/uploads',
  },

  /** MMSW's schedule for Northport, Southport and the other Port Klang terminals, read only (change mmsw-vessel-schedule). */
  MMSW_SCHEDULE: {
    LIST: '/api/mmsw/vessel-schedules',
    /** "Read MMSW now": Super Admins only. */
    READ_NOW: '/api/mmsw/vessel-schedules/read',
  },

  DASHBOARD: {
    SALES: (comId: IdParam) => `/api/dashboard/sales/${comId}`,
    EMPLOYEE_SALES: (comId: IdParam) => `/api/dashboard/employee-sales/${comId}`,
    EMPLOYEE_INVOICE: (comId: IdParam) => `/api/dashboard/employee-invoice/${comId}`,
    EXPENSE: (comId: IdParam) => `/api/dashboard/expense/${comId}`,
    EXPENSE_NAME: (comId: IdParam) => `/api/dashboard/expense-name/${comId}`,
    SUPPLIER_EXPENSE: (comId: IdParam) => `/api/dashboard/supplier-expense/${comId}`,
    FORWARDING: (comId: IdParam) => `/api/dashboard/forwarding/${comId}`,
    // Maintenance bills by kind and running costs by kind (legacy BillsOrderMaster/SelectStatusBO,
    // LoadExpenseData). ?fromDate=&toDate= (ISO).
    MAINTENANCE_STATUS: (comId: IdParam) => `/api/dashboard/maintenance-status/${comId}`,
    RUNNING_EXPENSES: (comId: IdParam) => `/api/dashboard/running-expenses/${comId}`,
    SALES_ORDER_STATUS: (comId: IdParam) => `/api/dashboard/sales-order-status/${comId}`,
    EMPLOYEE_RULES: (comId: IdParam) => `/api/dashboard/employee-rules/${comId}`,
    EMPLOYEE_PERFORMANCE: (comId: IdParam) => `/api/dashboard/employee-performance/${comId}`,
    WEEKLY_REPORT: (comId: IdParam) => `/api/dashboard/weekly-report/${comId}`,
    MONTHLY_SALE: (comId: IdParam) => `/api/dashboard/monthly-sale/${comId}`,
    PENDING_PAYMENT: (comId: IdParam) => `/api/dashboard/pending-payment/${comId}`,
    COMPLETED_PAYMENT: (comId: IdParam) => `/api/dashboard/completed-payment/${comId}`,
    /**
     * Billed sale orders grouped by customer — the Accounts Receivable desk's
     * "Completed" panel. Replaces the legacy POST /DashBoard/CompletedPaymentDB,
     * which despite its name reads no payment at all. Not COMPLETED_PAYMENT,
     * which is the payable side.
     */
    RECEIVABLE_BILLED: (comId: IdParam) => `/api/dashboard/receivable-billed/${comId}`,
    UNRELEASED: (comId: IdParam) => `/api/dashboard/unreleased/${comId}`,
    K8_UNRELEASED: (comId: IdParam) => `/api/dashboard/k8-unreleased/${comId}`,
    INVOICE_CHECK: (comId: IdParam) => `/api/dashboard/invoice-check/${comId}`,
    VESSEL_PLANNING: (comId: IdParam) => `/api/dashboard/vessel-planning/${comId}`,
    AIR_FREIGHT: (comId: IdParam) => `/api/dashboard/air-freight/${comId}`,
    EMPLOYEE_SALES_CURRENT_MONTH: (comId: IdParam) => `/api/dashboard/employee-sales-current-month/${comId}`,
    TOP_PERFORMERS: (limit: number) => `/api/dashboard/top-performers/${limit}`,
    ADMIN: '/api/dashboard/admin',
    SUPER_ADMIN: '/api/dashboard/super-admin',
    RTI_REPORT: '/api/rti/driver-report',
  },

  RTI: {
    LIST_BY_COMPANY: (companyId: IdParam) => `/api/rti-masters/company/${companyId}/active`,
    SEARCH_JOB_NO: (companyId: IdParam) => `/api/rti-masters/company/${companyId}/job-search`,
    SUMMARY: (companyId: IdParam) => `/api/rti-masters/company/${companyId}/summary`,
    GET: (id: IdParam) => `/api/rti-masters/${id}`,
    CREATE: '/api/rti-masters',
    UPDATE: (id: IdParam) => `/api/rti-masters/${id}`,
    DELETE: (id: IdParam) => `/api/rti-masters/${id}`,
    REVISE: (id: IdParam, companyId?: IdParam) => `/api/rti-masters/${id}/revise${companyId ? `?companyRefId=${companyId}` : ''}`,
    SHARE_WHATSAPP: (id: IdParam) => `/api/rti-masters/${id}/share-whatsapp`,
    /** The RTI report (driver's trip sheet), PDF - the Jasper port of CRRTIReport2. */
    REPORT: (id: IdParam) => `/api/rti-masters/${id}/report`,
    /** The same report parked behind a ticket whose link ends in the RTI number (download name). */
    REPORT_TICKET: (id: IdParam) => `/api/rti-masters/${id}/report-ticket`,
    /** The Driver RTI report (salary per driver and truck for a period) behind a ticket - the Jasper port of CRDriverRTiReport. */
    DRIVER_REPORT_TICKET: '/api/rti-masters/driver-report/ticket',
    /** The Driver RTI detailed report (one row per job) behind a ticket - the Jasper port of CRDriverRTiDetailedReport. */
    DRIVER_REPORT_DETAILED_TICKET: '/api/rti-masters/driver-report/detailed/ticket',
    /** The Driver RTI page's EXCEL: the driver salary workbook (.xlsx), plain and one row per job. */
    DRIVER_REPORT_EXCEL: '/api/rti-masters/driver-report/excel',
    DRIVER_REPORT_DETAILED_EXCEL: '/api/rti-masters/driver-report/detailed/excel',
    DETAILS_BY_MASTER: (id: IdParam) => `/api/rti-details/rti-master/${id}`,
    DETAIL_CREATE: '/api/rti-details',
    DETAIL_UPDATE: (id: IdParam) => `/api/rti-details/${id}`,
    DETAIL_DELETE_BY_MASTER: (id: IdParam) => `/api/rti-details/rti-master/${id}`,
    SEQUENCE_BY_COMPANY: (companyId: IdParam) => `/api/sequence-masters/company/${companyId}`,
    STATUS_BY_SALE_ORDERS: '/api/rti-details/rti-status',
    JOB_WISE_VIEW: '/api/rti/job-wise-view',
    EMPLOYEE_ASSIGNMENTS: '/api/rti/employee-assignments',
  },

 
 

  UOM: {
    LIST: '/api/uoms',
    GET: (id: IdParam) => `/api/uoms/${id}`,
    CREATE: '/api/uoms',
    UPDATE: (id: IdParam) => `/api/uoms/${id}`,
    DELETE: (id: IdParam) => `/api/uoms/${id}`,
    PROCESS: '/api/uoms/process',
  },

  // Bills moved to the Spring API on 2026-08-27. These go through `api`
  // (VITE_API_BASE_URL).
  BILL_MASTER: {
    NEXT_NUMBER: '/api/bills/next-number',
    SAVE: '/api/bills/save',
    EDIT: '/api/bills/edit',
    SEARCH: '/api/bills/search',
    DUE: '/api/bills/due',
    DELETE: '/api/bills',
    DESCRIPTIONS: '/api/bills/descriptions',
    PAID_AMOUNT: '/api/bills',
    INVOICE_NO_EXISTS: '/api/bills/invoice-no-exists',
    GET_SUPPLIER_CURRENCY: '/api/bills/supplier-currency',
    PUSH_QNE: '/api/bills',
  },

  // Purchase orders moved to the Spring API. Used by the push-to-bill and
  // push-to-payment-voucher hand-offs to load the PO being converted.
  BILLS_ORDER_MASTER: {
    EDIT: '/api/bills-order/edit-bills-order',
    SELECT_PAYMENT_TO: '/api/bills-order/select-payment-to',
  },

  // Chart of accounts. The tree is QNE's GLAccounts table (UUID keys), which
  // is why these are not the AccountsGroupMaster CRUD endpoints alongside them.
  CHART_OF_ACCOUNTS: {
    TREE: '/api/v1/accounts-group-master/gl-accounts/list',
    CLASSIFICATIONS: '/api/v1/accounts-group-master/classifications',
    SET_CLASSIFICATION: '/api/v1/accounts-group-master/gl-accounts/classification',
    IMPORT_FROM_QNE: '/api/v1/accounts-group-master/gl-accounts',
  },

  // Pay Bills moved to the Spring API on 2026-08-27, replacing /Payment/* and
  // the SP_Payment + RT_SupplierBills stored procedures. These go through
  // `api` (VITE_API_BASE_URL).
  // Customer receipts — Spring (/api/receipts), replacing the legacy
  // /Receipt/* MVC actions on 2026-09-07. PRINT is the Jasper voucher PDF
  // (legacy Crystal ReceiptReport); MAIL_INFO/MAIL replace ReceiptMailInfo/
  // SendReceiptMail. Attachments go through the shared /api/attachments
  // module with ATTACHMENT_FOLDERS.receipt.
  RECEIPT: {
    MAX_NO: (companyId: IdParam) => `/api/receipts/company/${companyId}/max-receipt-no`,
    SAVE: '/api/receipts/insert',
    EDIT: '/api/receipts/edit',
    SEARCH: '/api/receipts/search',
    DELETE: (id: IdParam) => `/api/receipts/${id}`,
    CUSTOMER_BILLS: '/api/receipts/customer-bills',
    CUSTOMER_BALANCE: '/api/receipts/customer-balance',
    PRINT: (id: IdParam) => `/api/receipts/${id}/print`,
    MAIL_INFO: (id: IdParam) => `/api/receipts/${id}/mail-info`,
    MAIL: (id: IdParam) => `/api/receipts/${id}/mail`,
    PUSH_QNE: (id: IdParam) => `/api/receipts/${id}/push-qne`,
  },

  /**
   * Sale credit notes — Spring (/api/sale-credits), replacing the legacy
   * /SaleCredit/* MVC actions. The legacy screen's single VIEW button pushed
   * to QNE, submitted to LHDN and opened a Crystal report in one call; each
   * is its own endpoint here, so a QNE failure no longer decides whether the
   * operator gets paper. CUSTOMER_BILLS is the credit note's own copy of the
   * outstanding-documents query: unlike RECEIPT.CUSTOMER_BILLS it excludes
   * the credit note being edited rather than a knock-off line id.
   */
  SALE_CREDIT: {
    NEXT_NO: (companyId: IdParam) => `/api/sale-credits/company/${companyId}/next-no`,
    CUSTOMER_BILLS: '/api/sale-credits/customer-bills',
    SAVE: '/api/sale-credits/insert',
    EDIT: '/api/sale-credits/edit',
    SEARCH: '/api/sale-credits/search',
    DELETE: (id: IdParam) => `/api/sale-credits/${id}/entry`,
    INVOICE_LOOKUP: '/api/sale-credits/invoice-lookup',
    PRINT: (id: IdParam) => `/api/sale-credits/${id}/print`,
    PUSH_QNE: (id: IdParam) => `/api/sale-credits/${id}/push-qne`,
    PUSH_EINVOICE: (id: IdParam) => `/api/sale-credits/${id}/push-einvoice`,
    EINVOICE_STATUS: (id: IdParam) => `/api/sale-credits/${id}/einvoice-status`,
  },

  PAYMENT: {
    NEXT_NUMBER: '/api/payments/next-number',
    SAVE: '/api/payments/save',
    EDIT: '/api/payments/edit',
    SEARCH: '/api/payments/search',
    DUE: '/api/payments/due',
    DELETE: '/api/payments',
    COMPLETE: '/api/payments',
    PUSH_QNE: '/api/payments',
    SUPPLIER_BILLS: '/api/payments/supplier-bills',
    SUPPLIER_BALANCE: '/api/payments/supplier-balance',
    PAYMENT_TERMS_DUE: '/api/payments/payment-terms-due',
    // The Jasper Pay Bills PDF (needs `companyId`), replacing the Crystal
    // /api/PaymentApp/PAYMENTVIEW on the legacy .NET host.
    PRINT: (id: IdParam) => `/api/payments/${id}/print`,

    // Attachments deliberately stay on the legacy .NET host: the old screen
    // is still in use and both front ends have to read the same PayBills
    // folder. Do not point these at Spring.
    FETCH_FILE: '/api/Common/FetchFile2',
    UPLOAD_FILE: '/api/Common/UploadFile3',
  },

  // Payment vouchers moved to the Spring API on 2026-08-27, replacing the
  // legacy /PaymentVoucherApp/* paths (which nothing served on this host —
  // the old constants 404'd against Spring). These go through `api`.
  PAYMENT_VOUCHER: {
    SELECT_COA_EXPENSE: '/api/payment-vouchers/select-coa-expense',
    NEXT_NUMBER: '/api/payment-voucher-masters/next-number',
    SAVE: '/api/payment-voucher-masters/save',
    EDIT: '/api/payment-voucher-masters/edit',
    SEARCH: '/api/payment-voucher-masters/search',
    DELETE: '/api/payment-voucher-masters',
    PUSH_QNE: '/api/payment-voucher-masters',
    DESCRIPTIONS: '/api/payment-voucher-masters/descriptions',
    SELECT_PAYMENT_FROM: '/api/payment-voucher-masters/select-payment-from',
    SELECT_PAYMENT_TO: '/api/payment-voucher-masters/select-payment-to',
    CLASSIFICATIONS: '/api/v1/accounts-group-master/classifications',

    // The Jasper voucher PDF (needs `companyId`), replacing both Crystal routes on
    // the legacy .NET host: /api/PaymentVoucherApp/PAYMENTVOUCHERVIEW and the
    // /PaymentVoucher/PaymentVoucherVIEW session stash + ReportViewer.aspx.
    PRINT: (id: IdParam) => `/api/payment-voucher-masters/${id}/print`,
    // Spring: GET ?name= — replaced the legacy /PaymentReceiptInfo/CheckCompanyname.
    CHECK_PAYTO: '/api/payment-receipt-infos/lookup',
  },

  // Petty cash moved to the Spring API. Brand-new screen — no legacy
  // PascalCase consumers to translate for, unlike PAYMENT_VOUCHER above.
  PETTY_CASH: {
    NEXT_NUMBER: '/api/petty-cash-masters/next-number',
    SAVE: '/api/petty-cash-masters/save',
    EDIT: '/api/petty-cash-masters/edit',
    SEARCH: '/api/petty-cash-masters/search',
    // Soft-delete (Active=2), company-scoped: `companyId` is required and the
    // server refuses another company's record. The lines hang off a plain FK
    // with no cascade, so a hard delete would strand them.
    DELETE: '/api/petty-cash-masters',
    // The Jasper report PDF (needs `companyId`), replacing the Crystal
    // /api/PettyCashApp/PETTYCASHVIEW on the legacy .NET host.
    PRINT: (id: IdParam) => `/api/petty-cash-masters/${id}/print`,
  },

  /**
   * Transaction reports. Replaces the legacy `/TransactionReport/*` MVC
   * actions as each screen is migrated.
   */
  TRANSACTION_REPORT: {
    /**
     * The Payment Completed grid — settled payment vouchers unioned with
     * settled supplier payments. POST because the category filter is a list.
     * Replaces `POST /TransactionReport/SelectPaymentDone`.
     */
    PAYMENT_DONE: '/api/transaction-reports/payment-done',
    /**
     * The printed Payment Completed report (PDF, Jasper) for the same body as
     * `PAYMENT_DONE`. Replaces the .NET stash + ReportViewer `PaymentCompletedReport`.
     */
    PAYMENT_DONE_REPORT: '/api/transaction-reports/payment-done/report',
  },

  PAYMENT_TERMS: {
    SELECT_ALL: '/api/PaymentTermsMaster/SelectPaymentTerms',
    /**
     * Credit days for a payment term. The `supplierId` parameter is really a
     * PaymentTermsMaster id - the legacy name is wrong and both the .NET SQL
     * and the Java port look it up that way, so it is kept as-is.
     */
    SUPPLIER_DUE: '/api/payment-terms/supplier-due',
  },

  SUPPLIER: {
    GET: '/api/SupplierMaster/GetSupplier',
    SELECT: '/api/SupplierMaster/SelectSupplier',
    SELECT_ALL: '/api/SupplierMaster/SelectSupplierAll',
    COMBO: '/api/suppliers/combo',
    /** The Supplier master screen (AddSupplier.js / AllSupplier.js) on Spring. */
    /** The SupplierView grid: keyword, column filters, type, active, sort and paging on the server. */
    SEARCH: '/api/suppliers/search',
    /** "Update from QNE" — legacy UpdateSupplierId. */
    QNE_SYNC: '/api/suppliers/qne/sync',
    BY_ID: (id: IdParam) => `/api/suppliers/${id}`,
    CREATE: '/api/suppliers',
    UPDATE: (id: IdParam) => `/api/suppliers/${id}`,
    SOFT_DELETE: (id: IdParam) => `/api/suppliers/${id}/soft-delete`,
    MSIC_CODES: '/api/suppliers/msic-codes',
    SELF_BILLED_TYPES: '/api/suppliers/self-billed-types',
    /** The supplier list's "Push to QNE" for a supplier the save's push missed. */
    PUSH_QNE: (id: IdParam) => `/api/suppliers/${id}/push-qne`,
  },

  INVENTORY: {
    // Item catalogue
    ITEMS: '/api/inventory/items',
    ITEM: (id: IdParam) => `/api/inventory/items/${id}`,
    ITEM_BY_PRODUCT: (productRefId: IdParam) => `/api/inventory/items/by-product/${productRefId}`,
    ITEM_LOW_STOCK: '/api/inventory/items/low-stock',
    ITEM_UOMS: '/api/inventory/items/uoms',
    // Already-deployed fallback, used when the inventory service predates ITEM_UOMS.
    UOM_LIST_ALL: '/api/uoms',
    // Stock movements
    STOCK_IN: '/api/inventory/stock-in',
    STOCK_OUT: '/api/inventory/stock-out',
    BALANCE: '/api/inventory/balance',
    LEDGER: '/api/inventory/ledger',
    TRUCK_USAGE: '/api/inventory/truck-usage',
    // Serialised units
    ASSETS: '/api/inventory/assets',
    ASSET_REGISTER: '/api/inventory/assets/register',
    ASSET_ISSUE: '/api/inventory/assets/issue',
    ASSET_RETURN_FOR_REPAIR: '/api/inventory/assets/return-for-repair',
    ASSET_MARK_REPAIRED: '/api/inventory/assets/mark-repaired',
    ASSET_BY_STATUS: (status: string) => `/api/inventory/assets/status/${status}`,
    ASSET_HISTORY: (serialNo: string) =>
      `/api/inventory/assets/${encodeURIComponent(serialNo)}/history`,
  },

  // Mailbox monitor (Super Admin): unread mail per company mailbox.
  // See maleva-backend/openspec/changes/add-mail-monitor.
  MAIL_MONITOR: {
    // What the caller may do: { level: 'ALL' } (Super Admin) or { level: 'TEAM' } (a team leader).
    ACCESS: '/api/mail-monitor/access',
    MAILBOXES: '/api/mail-monitor/mailboxes',
    MAILBOX: (id: IdParam) => `/api/mail-monitor/mailboxes/${id}`,
    HISTORY: (id: IdParam) => `/api/mail-monitor/mailboxes/${id}/history`,
    PREVIEW: (id: IdParam) => `/api/mail-monitor/mailboxes/${id}/unread-preview`,
    UNREAD: (id: IdParam) => `/api/mail-monitor/mailboxes/${id}/unread`,
    MESSAGE: (id: IdParam, uid: IdParam) => `/api/mail-monitor/mailboxes/${id}/messages/${uid}`,
    CREDENTIAL: (id: IdParam) => `/api/mail-monitor/mailboxes/${id}/credential`,
    TEST: (id: IdParam) => `/api/mail-monitor/mailboxes/${id}/test`,
    REMIND: (id: IdParam) => `/api/mail-monitor/mailboxes/${id}/remind`,
    CHECK_NOW: '/api/mail-monitor/check-now',
    IMPORT_CANDIDATES: '/api/mail-monitor/import-candidates',
    // Reply times per mailbox (add-mail-response-report), Super Admin only.
    RESPONSE_REPORT: '/api/mail-monitor/response-report',
    RESPONSE_LATE: '/api/mail-monitor/response-report/late',
    // The mails behind a mailbox's numbers (mail-count-own-mail).
    RESPONSE_MAILS: '/api/mail-monitor/response-report/mails',
    // Correct who a mail was for; the corrections for review (mail-team-credit-and-corrections).
    RESPONSE_CORRECTION: (id: IdParam) => `/api/mail-monitor/response-report/mails/${id}/correction`,
    RESPONSE_CORRECTIONS: '/api/mail-monitor/response-report/corrections',
    RESPONSE_SUMMARY: '/api/mail-monitor/response-report/summary',
    RESPONSE_SETTINGS: (id: IdParam) => `/api/mail-monitor/mailboxes/${id}/response-settings`,
    AFTER_HOURS: '/api/mail-monitor/after-hours',
    // Booking mail -> sale order -> job-number reply per employee (mail-reading-and-booking-speed).
    BOOKING_SPEED: '/api/mail-monitor/booking-speed',
    BOOKING_SUMMARY: '/api/mail-monitor/booking-speed/summary',
    // The Super Admin's check of a booking-like mail (2026-10-12).
    BOOKING_CHECK: (id: IdParam) => `/api/mail-monitor/booking-speed/mails/${id}/booking`,
    // Office calendar of the reports (mail-report-office-hours-fair-ranking), Super Admin only.
    HOLIDAYS: '/api/mail-monitor/holidays',
    HOLIDAY: (day: string) => `/api/mail-monitor/holidays/${day}`,
    // The signed-in employee's own linked mailboxes and their latest notice (every role).
    MY_UNREAD: '/api/mail-monitor/my-unread',
    RESPONSE_PDF: '/api/mail-monitor/response-report/pdf',
    AFTER_HOURS_PDF: '/api/mail-monitor/after-hours/pdf',
    TOPIC: (companyId: IdParam) => `/topic/mail-monitor/${companyId}`,
  },

  RECON: {
    // Remove a failed unit from a truck and fit its replacement, atomically.
    SWAP: '/api/inventory/recon/swap',
    LIST: '/api/inventory/recon',
    JOB: (id: IdParam) => `/api/inventory/recon/${id}`,
    // The recon shelf: removed but not yet finished.
    OPEN: '/api/inventory/recon/open',
    SEND: (id: IdParam) => `/api/inventory/recon/${id}/send`,
    COSTS: (id: IdParam) => `/api/inventory/recon/${id}/costs`,
    COST: (id: IdParam, costId: IdParam) => `/api/inventory/recon/${id}/costs/${costId}`,
    COMPLETE: (id: IdParam) => `/api/inventory/recon/${id}/complete`,
    SCRAP: (id: IdParam) => `/api/inventory/recon/${id}/scrap`,
    BY_TRUCK: (truckRefId: IdParam) => `/api/inventory/recon/truck/${truckRefId}`,
    BY_ASSET: (assetRefId: IdParam) => `/api/inventory/recon/asset/${assetRefId}`,
    SUMMARY_TRUCK: '/api/inventory/recon/summary/truck',
  },

  FUEL_ENTRY: {
    LIST: '/api/fuel-entries',
    NEXT_NO: '/api/fuel-entries/next-no',
    GET: (id: IdParam) => `/api/fuel-entries/${id}`,
    PRINT_DATA: (id: IdParam) => `/api/fuel-entries/${id}/print-data`,
    /** The printed report (PDF, Jasper) for the same filters as `LIST`. */
    REPORT: '/api/fuel-entries/report',
    /** "Monthly Report": the fuel report with the PV / subsidy / Patron / App / GPS totals on top. Needs dates. */
    MONTHLY_REPORT: '/api/fuel-entries/monthly-report',
    SAVE: '/api/fuel-entries',
    DELETE: (id: IdParam) => `/api/fuel-entries/${id}`,
  },

  FLEET: {
    /** Expiry alerts for trucks and drivers, ranked by urgency. */
    MAINTENANCE_DASHBOARD: '/api/fleet/maintenance-dashboard',
    MAINTENANCE_SPEND: '/api/fleet/maintenance-spend',
  },

  TOLL_ENTRY: {
    LIST: '/api/toll-entries',
    NEXT_NO: '/api/toll-entries/next-no',
    GET: (id: IdParam) => `/api/toll-entries/${id}`,
    PRINT_DATA: (id: IdParam) => `/api/toll-entries/${id}/print-data`,
    /** The printed report (PDF, Jasper) for the same filters as `LIST`. */
    REPORT: '/api/toll-entries/report',
    SAVE: '/api/toll-entries',
    DELETE: (id: IdParam) => `/api/toll-entries/${id}`,
  },

  // The Truck Order Calendar, replacing the legacy /TruckMaster/* order actions.
  // TRUCKS is narrower than TRUCK.COMBO on purpose: the calendar books only
  // trucks flagged TruckMaster.OrderableTruck = 1, which the legacy screen
  // hardcoded as a plate list in JS.
  TRUCK_ORDER: {
    LIST: '/api/truck-orders',
    TRUCKS: '/api/truck-orders/trucks',
    NEXT_NO: '/api/truck-orders/next-no',
    CLASH: '/api/truck-orders/clash',
    // Place names already used as origin / destination, most used first.
    PLACES: '/api/truck-orders/places',
    // Free / taken own trucks on one day, optionally for one size class.
    AVAILABILITY: '/api/truck-orders/availability',
    GET: (id: IdParam) => `/api/truck-orders/${id}`,
    SAVE: '/api/truck-orders',
    DELETE: (id: IdParam) => `/api/truck-orders/${id}`,
  },

  // Truck & Driver Details sheet: a truck and a driver on one page (read only;
  // the values are edited on Truck Master and Driver Master).
  TRUCK_DRIVER_DETAILS: {
    GET: '/api/truck-driver-details',
  },

  // The weekly Truck Location Board. A new screen: no legacy endpoint behind it.
  // One path, GET reads a week and POST saves the changed cells.
  TRUCK_LOCATION: {
    WEEK: '/api/truck-locations/week',
    // The row order every planner shares. Saved on its own, the moment a row is dragged.
    ORDER: '/api/truck-locations/order',
    // Places typed on the Planning page (long-press ORIGIN / DEST), sent after the plan saved.
    PLANNING_STOPS: '/api/truck-locations/planning-stops',
  },

  // GPS data captured from Wialon. The sync job is the only writer; these are reads.
  GPS: {
    FUEL_FILLINGS: '/api/gps/fuel-fillings',
    SPEED_REPORTS: '/api/gps/speed-reports',
    ENGINE_HOURS: '/api/gps/engine-hours',
    /** One-to-one assignment of GPS fillings to the fuel entries of a truck-day. */
    FUEL_MATCH: '/api/gps/fuel-match',
    SYNC: '/api/gps/sync',
  },

  /**
   * AI document reading. `EXTRACT_BILL` and `EXTRACT_PURCHASE_ORDER` take a
   * multipart part named `file` (PDF, PNG, JPG or WEBP, max 10 MB) plus
   * `companyId`, and answer with the supplier, header and lines they read so
   * the bills / purchase order form can be prefilled for review. Optional
   * `provider` forces a provider instead of the configured one.
   */
  AI: {
    EXTRACT_BILL: '/api/ai/bills/extract',
    EXTRACT_PURCHASE_ORDER: '/api/ai/purchase-orders/extract',
    // Planning: truck + driver suggestions for the loaded grid rows, and the
    // fire-and-forget record of what the user finally saved against them.
    PLANNING_SUGGEST: '/api/ai/planning/suggest',
    PLANNING_SUGGEST_FEEDBACK: '/api/ai/planning/suggest/feedback',
    // Read-only: compares the AI's plan with the plans saved for a date range.
    PLANNING_REPLAY: '/api/ai/planning/replay',
  },

  /**
   * The LLM provider catalogue, per-company routing settings, a one-prompt
   * connectivity test, and the recent call log behind the AI Settings screen.
   * Settings writes are admin-only on the server (roles 100 / 200).
   */
  LLM: {
    PROVIDERS: '/api/llm/providers',
    SETTINGS: '/api/llm/settings',
    TEST: '/api/llm/test',
    CALLS: '/api/llm/calls',
  },
  /** Per-role access to controlled screens. The rules are Super Admin only; MINE is open to all. */
  SCREEN_ACCESS: {
    RULES: (screen: string) => `/api/screen-access/${screen}`,
    MINE: (screen: string) => `/api/screen-access/${screen}/me`,
  },
} as const;

export default API_ENDPOINTS;
